import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Message } from '../../../../classes/Message';
import { includes, lowerCase, uniqBy } from 'lodash';
import { IJobScreenEvent, JobsService, TaskService, UserService } from '../../../../services';
import { EventService } from '../../../../services';
import { Subscription } from 'rxjs';
import { RefreshJobEventTypeEnum } from '../../../../enums/refresh-job-event-type.enum';
import { MessageService } from 'primeng';
import { OrganisationTypeEnum } from '../../../../enums/organisation-types';
import { IJobHistory } from '../../../../interfaces/job/job-history';
import { JobScreenHistoryJob } from '../../../../interfaces/screen-specific/job-screen';

@Component({
  selector: 'app-job-screen-history',
  styleUrls: ['job-screen-history.component.scss', '../job-screen-common.scss'],
  templateUrl: 'job-screen-history.component.html',
})
export class JobScreenHistoryComponent implements OnInit, OnDestroy {
  public isLoading: boolean = false;

  @Input() panelHeight: string = '400px';
  @Input() jobId!: number;
  @Input('organisationId') organisationId!: number;
  @Input('organisationTypeId') organisationTypeId!: number;

  subscriptions: Subscription[] = [];

  newNoteIsCustomer: boolean = false;
  newNoteIsSupplier: boolean = false;
  newNoteIsEngineius: boolean = false;
  filterEngineius: boolean = true;
  filterCustomer: boolean = true;
  filterSupplier: boolean = true;
  historys: IJobHistory[] = [];
  fullHistorys: any[];
  newHistoryNote: string;
  filterHistorySearch: string = '';

  selectedEventType: any;
  eventTypes: any[] = [];

  selectedEventTask: any;
  eventTasks: any[] = [];

  tasksChosen: number[] = [null];
  taskOptions: { label: string; value: number }[] = [];
  private previousValues: number[] = [null];

  protected readonly OrganisationTypeEnum = OrganisationTypeEnum;

  constructor(
    private jobService: JobsService,
    private taskService: TaskService,
    private messageService: MessageService,
    private eventService: EventService,
  ) {}

  async ngOnInit() {
    this.loadTasks();
    this.loadHistorys();

    this.subscriptions.push(
      this.eventService.RefreshJobEvent.subscribe((eventType: RefreshJobEventTypeEnum) => {
        if (eventType == RefreshJobEventTypeEnum.RefreshHistory) {
          this.loadHistorys();
        }
      }),
    );

    this.subscriptions.push(
      this.eventService.JobScreenEvent.subscribe((event) => {
        this.handleEvents(event);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x?.unsubscribe());
  }

  loadTasks() {
    let cnt = 0;

    this.jobService
      .getForComponent(this.jobId, 'vme-job-screen-history')
      .then((result: JobScreenHistoryJob) => {
        // Get a list of tasks
        this.taskOptions = result.orders.reduce((acc, order) => {
          return acc.concat(
            order.tasks.map((task) => {
              cnt++;
              return {
                label: 'Task ' + cnt + ': ' + task.vehicle.registrationNumber,
                value: task.id,
              };
            }),
          );
        }, []);

        this.taskOptions.unshift({ label: 'All tasks', value: null });
      });
  }

  loadHistorys() {
    this.isLoading = true;
    this.historys = [];
    this.fullHistorys = [];
    this.eventTypes = [];
    this.eventTasks = [];
    this.filterHistorySearch = '';

    this.jobService
      .getHistory(this.jobId)
      .then((result) => {
        this.isLoading = false;
        this.fullHistorys = result;
        this.historys = result;

        this.eventTypes.push({ label: 'All event types', value: null });

        let classes = uniqBy(this.historys, 'area');
        classes.forEach((x) => {
          this.eventTypes.push({ label: x.area, value: x.area });
        });

        this.eventTasks.push({ label: 'Select task', value: null });
        let tasks = uniqBy(this.historys, 'taskName');
        tasks.forEach((x) => {
          if (x.taskName) this.eventTasks.push({ label: x.taskName, value: x.taskName });
        });
      })
      .catch(() => {
        this.isLoading = false;
        this.messageService.add(
          Message.Error('An error occurred while loading Job History.', 'Load History'),
        );
      });
  }

  getRegPlate(plate: string) {
    if (plate.length == 7) {
      return plate.substr(0, 4) + ' ' + plate.substr(4);
    } else {
      return plate;
    }
  }

  filterEventTypeChanged() {
    this.filterHistorys();
  }

  filterHistorys() {
    let tags: string[] = ['Engineius', 'Customer', 'Supplier'];

    if (!this.filterEngineius) tags = tags.filter((x) => x != 'Engineius');
    if (!this.filterCustomer) tags = tags.filter((x) => x != 'Customer');
    if (!this.filterSupplier) tags = tags.filter((x) => x != 'Supplier');

    const lowerCaseTags = tags.map((x) => lowerCase(x));

    this.historys = this.fullHistorys.filter((x) => includes(lowerCaseTags, x.tagType));
    this.historys = this.historys.filter(
      (x) => x.area == this.selectedEventType || !this.selectedEventType,
    );
    this.historys = this.historys.filter(
      (x) => x.taskName == this.selectedEventTask || !this.selectedEventTask || !x.taskName,
    );

    if (this.filterHistorySearch.trim().length > 0) {
      const escapedFilterText = this.filterHistorySearch.replace(/([.+?^${}()|\[\]\/\\])/g, '\\$1');
      const regexPattern = new RegExp(escapedFilterText.replace(/\*/g, '.*'), 'i');

      this.historys = this.historys.filter((x) => regexPattern.test(x.description));
    }
  }

  addNewNote() {
    let viewIsEngineius = false;
    let viewIsCustomer = false;
    let viewIsSupplier = false;

    if (this.organisationTypeId == OrganisationTypeEnum.Engineius) {
      viewIsEngineius = this.newNoteIsEngineius;
      viewIsCustomer = this.newNoteIsCustomer;
      viewIsSupplier = this.newNoteIsSupplier;
    } else if (this.organisationTypeId == OrganisationTypeEnum.Customer) {
      viewIsCustomer = true;
    } else if (this.organisationTypeId == OrganisationTypeEnum.Supplier) {
      viewIsSupplier = true;
    }

    let d = new Date();

    let taskIds = this.tasksChosen || [null];

    let promises = [];

    taskIds.forEach((taskId: number) => {
      promises.push(
        this.jobService.addHistory({
          jobId: this.jobId,
          taskId: taskId,
          note: this.newHistoryNote,
          viewIsEngineius: viewIsEngineius,
          viewIsCustomer: viewIsCustomer,
          viewIsSupplier: viewIsSupplier,
          createdOn: d,
        }),
      );
    });

    Promise.all(promises)
      .then(() => {
        this.newHistoryNote = '';
        this.newNoteIsEngineius = false;
        this.newNoteIsCustomer = false;
        this.newNoteIsSupplier = false;
        this.loadHistorys();
      })
      .catch(() => {
        this.messageService.add(
          Message.Error(
            'An error occurred while updating the data. The current data may not be correct.',
            'Add New Note',
          ),
        );
      });
  }

  private handleEvents(event: IJobScreenEvent) {
    if (event.event == 'refresh-notes' && event.data.jobId == this.jobId) {
      this.loadHistorys();
    }

    if (event.event == 'sequence-update') {
      this.loadHistorys();
    }
  }

  setTasksChosen(event: any) {
    // What has been added to array
    let changes = this.tasksChosen.filter((x) => !this.previousValues.includes(x))[0];

    console.log(changes);

    if (changes === null) {
      console.log('RESETTING TO NULL');
      this.tasksChosen = [null];
    } else if (!isNaN(changes)) {
      console.log('REMOVING NULL');
      this.tasksChosen = this.tasksChosen.filter((x) => x != null);
    }

    this.previousValues = this.tasksChosen;
  }
}
